import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { AdminUserService } from '../services/admin-user.service';
import { firstValueFrom, timeout, catchError, of, take } from 'rxjs';

export const authGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const adminUserService = inject(AdminUserService);
  const router = inject(Router);

  // Wait for Firebase auth state to initialize (with 2 second timeout)
  let currentUser;
  try {
    currentUser = await firstValueFrom(
      authService.user$.pipe(
        take(1),
        timeout(2000),
        catchError(() => {
          // If timeout or error, fall back to synchronous check
          return of(authService.getCurrentUser());
        })
      )
    );
  } catch (error) {
    console.error('Error waiting for auth state:', error);
    currentUser = authService.getCurrentUser();
  }
  
  if (!currentUser) {
    return router.createUrlTree(['/login']);
  }

  try {
    // Fetch admin user document by email
    const email = currentUser.email;
    if (!email) {
      await authService.signOut();
      return router.createUrlTree(['/login']);
    }

    const adminUsers = await firstValueFrom(
      adminUserService.getUserByEmail(email)
    );

    // Validate admin user document exists
    if (!adminUsers || adminUsers.length === 0) {
      await authService.signOut();
      return router.createUrlTree(['/login']);
    }

    const adminUser = adminUsers[0];

    // Validate admin user has required fields
    if (!adminUser.status || !adminUser.role) {
      await authService.signOut();
      return router.createUrlTree(['/login']);
    }

    // Validate admin user status is 'active'
    if (adminUser.status !== 'active') {
      await authService.signOut();
      return router.createUrlTree(['/login']);
    }

    // User is valid and active
    // All authenticated users now use the unified /admin layout
    // Module visibility is handled by the ModuleVisibilityService
    return true;
  } catch (error: any) {
    await authService.signOut();
    return router.createUrlTree(['/login']);
  }
};

